import React, { useEffect, useMemo } from "react";

import { useChat } from "../../../context/contextChat";
import { useAwsConfig } from "../../../context/Dashboard/aws/contextConfig";
import { useAwsIp } from "../../../context/Dashboard/aws/contextIp";
import { useAwsUrl } from "../../../context/Dashboard/aws/contextUrl";
import { useAzureConfig } from "../../../context/Dashboard/azure/contextConfig";
import { useAzureIp } from "../../../context/Dashboard/azure/contextIp";
import { useAzureUrl } from "../../../context/Dashboard/azure/contextUrl";
import { useGcpConfig } from "../../../context/Dashboard/gcp/contextConfig";
import { useGcpIp } from "../../../context/Dashboard/gcp/contextIp";
import { useGcpUrl } from "../../../context/Dashboard/gcp/contextUrl";
import { useOciConfig } from "../../../context/Dashboard/oci/contextConfig";
import { useOciIp } from "../../../context/Dashboard/oci/contextIp";
import { useOciUrl } from "../../../context/Dashboard/oci/contextUrl";
import {
  buildCloudEvidence,
  cloudFindingsCsv,
  downloadTextFile,
} from "../../../pages/Dashboard/cloud/cloudModel";

const PROVIDER_LABELS = {
  aws: "AWS",
  azure: "Azure",
  gcp: "GCP",
  oci: "OCI",
};

const FINDING_TYPES = {
  configuration: "Misconfiguration",
  network: "Network Vulnerability",
  web: "Web Vulnerability",
};

const CloudProviderReport = ({ providerId, reportType }) => {
  const { openVulnerabilityActions } = useChat();
  const aws = useAwsConfig();
  const awsIp = useAwsIp();
  const awsUrl = useAwsUrl();
  const azure = useAzureConfig();
  const azureIp = useAzureIp();
  const azureUrl = useAzureUrl();
  const gcp = useGcpConfig();
  const gcpIp = useGcpIp();
  const gcpUrl = useGcpUrl();
  const oci = useOciConfig();
  const ociIp = useOciIp();
  const ociUrl = useOciUrl();

  const provider = {
    aws: {
      config: aws,
      configSource: aws?.cAwsConfigAll || aws?.configAssets,
      ip: awsIp,
      url: awsUrl,
    },
    azure: {
      config: azure,
      configSource: azure?.cAzureConfigAll || azure?.configAssets,
      ip: azureIp,
      url: azureUrl,
    },
    gcp: {
      config: gcp,
      configSource: gcp?.cGcpConfigAll || gcp?.configAssets,
      ip: gcpIp,
      url: gcpUrl,
    },
    oci: {
      config: oci,
      configSource: oci?.cOciConfigAll || oci?.configAssets,
      ip: ociIp,
      url: ociUrl,
    },
  }[providerId];

  useEffect(() => {
    Promise.allSettled([
      provider?.config?.initData?.(),
      provider?.ip?.initData?.(),
      provider?.url?.initData?.(),
    ]).catch(() => undefined);
    // Context methods are recreated during state updates; the provider report
    // only needs one hydration pass when its selected provider changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const evidence = useMemo(
    () =>
      buildCloudEvidence({
        providerId,
        configSource: provider?.configSource,
        networkResults: provider?.ip?.networkResults || [],
        webResults: provider?.url?.webResults || [],
      }),
    [
      providerId,
      provider?.configSource,
      provider?.ip?.networkResults,
      provider?.url?.webResults,
    ]
  );
  const findingType = FINDING_TYPES[reportType];
  const findings = evidence.findings.filter(
    (finding) => finding.findingType === findingType
  );
  const providerLabel = PROVIDER_LABELS[providerId] || providerId.toUpperCase();

  const downloadCsv = () =>
    downloadTextFile(
      cloudFindingsCsv(findings),
      `SAVE_${providerLabel}_${reportType}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
      "text/csv;charset=utf-8"
    );

  return (
    <div className="d-flex flex-column h-100 overflow-hidden">
      <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
        <div>
          <h5 className="fw-bold mb-1">{providerLabel} {findingType} Report</h5>
          <p className="text-muted small mb-0">
            Normalized SAVE cloud assessment evidence. Missing intelligence is
            shown as unavailable and is not inferred.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={!findings.length}
          onClick={downloadCsv}
        >
          <i className="bi bi-download me-2" />Export CSV
        </button>
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <span className="badge text-bg-secondary">Assets: {evidence.assets.length}</span>
        <span className="badge text-bg-danger">Findings: {findings.length}</span>
        <span className="badge text-bg-info">Controls: {evidence.compliance.length}</span>
        <span className="badge text-bg-warning">Attack paths: {evidence.attackPaths.length}</span>
      </div>

      {!findings.length ? (
        <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-muted">
          <i className="bi bi-cloud-slash display-3 mb-3" />
          <h5>No {findingType.toLowerCase()} evidence is available</h5>
          <span>Run the corresponding authorized cloud assessment first.</span>
        </div>
      ) : (
        <div className="flex-grow-1 overflow-auto">
          <table className="table table-hover align-middle">
            <thead className="position-sticky top-0 bg-white">
              <tr>
                <th>Severity</th>
                <th>Service / target</th>
                <th>Finding</th>
                <th>Evidence</th>
                <th>CVSS</th>
                <th>EPSS</th>
                <th>AI</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((finding) => (
                <tr key={finding.id}>
                  <td><span className="badge text-bg-danger">{finding.severity}</span></td>
                  <td>{finding.target || finding.service}</td>
                  <td>{finding.findingName}</td>
                  <td>{finding.flagged}</td>
                  <td>{finding.cvss ?? "—"}</td>
                  <td>{finding.epss ?? "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => openVulnerabilityActions(finding)}
                    >
                      <i className="bi bi-robot" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const AzureConfigReport = () => <CloudProviderReport providerId="azure" reportType="configuration" />;
export const AzureNetworkReport = () => <CloudProviderReport providerId="azure" reportType="network" />;
export const AzureWebReport = () => <CloudProviderReport providerId="azure" reportType="web" />;
export const GcpConfigReport = () => <CloudProviderReport providerId="gcp" reportType="configuration" />;
export const GcpNetworkReport = () => <CloudProviderReport providerId="gcp" reportType="network" />;
export const GcpWebReport = () => <CloudProviderReport providerId="gcp" reportType="web" />;
export const OciConfigReport = () => <CloudProviderReport providerId="oci" reportType="configuration" />;
export const OciNetworkReport = () => <CloudProviderReport providerId="oci" reportType="network" />;
export const OciWebReport = () => <CloudProviderReport providerId="oci" reportType="web" />;

export default CloudProviderReport;
